/**
 * 控制台的数据层 —— 全部来自自家 oxman-hub,没有第二个后端。
 *
 * 三条规矩(design-fleet-console-implementation.md §3「诚实优先」):
 *
 *  1. **读失败绝不降级成 mock。** 原型的 useSupabaseData 在 catch 里塞 mock 数据,
 *     于是"后端挂了"和"一切正常"在台面上长得一模一样 —— 这正是 §9b 那条
 *     「我测不到 ≠ 它不存在」在前端的复现。这里失败就是 error,页面显示错误。
 *  2. **hub 没有的数据源不假装有。** 告警/认知/运维还没有表(P2–P4),
 *     对应 hook 返回 `unavailable`,页面画"暂无数据源",不编好看的假数字。
 *  3. **缺的字段是 null。** 电量/温度/版本是遥测,hub v0 不存 → null,不是 0。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { hub, connectEvents, HubError, type HubDevice, type HubNode, type HubUnit, type WGPeer,
  type HubTelemetry, type HubAlert, type HubAuditEntry, type HubSummary,
  type HubKnowledgeGraph, type HubKnowledgeSummary, type HubVideoDevice,
  type HubMapGrid, type HubLivePose, type HubDeviceReport, mapGridURL, auth } from '@/lib/hubClient';
import { isoFromUnixSec, isoFromUnixMs } from '@/lib/format';
import type { Terminal, Task, TaskUnit } from '@/types/fleet';

/** 一个数据源的三态。unavailable 是"本期没有这个源",和 error 分开。 */
export interface Source<T> {
  data: T;
  loading: boolean;
  /**
   * ★ `loading` 与 `loaded` 是两件事, 页面要区分开:
   *   `loading`  这一刻正在取(**包括 refetch**)
   *   `loaded`   取到过至少一次(首屏已经能画了)
   *
   * 2026-09-11 的事故就出在把这两件事当成一件: 设备页写的是 `if (loading) 转圈`,
   * 于是**任何一次 refetch 都会把整棵子树卸载重建**。轮换令牌的流程正好是
   * "拿到一次性令牌 → 顺手 refetch 列表", 结果令牌刚 setState 进组件本地状态,
   * 下一帧组件就被卸载了 —— 用户点完确认只看到抽屉整个消失, 而服务端那把旧令牌
   * 已经作废。**每点一次烧掉一把令牌, 且一次都看不到。**
   *
   * ⇒ 首屏转圈看 `loaded`, 不看 `loading`。`loading` 只配画一个不卸载内容的细进度条。
   */
  loaded: boolean;
  error: string | null;
  unavailable: string | null;
  refetch: () => void;
}

function useFetch<T>(fn: () => Promise<T>, deps: unknown[] = []): Source<T | null> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(() => {
    let alive = true;
    setLoading(true);
    fnRef.current()
      .then((d) => { if (alive) { setData(d); setError(null); setUnavailable(null); } })
      .catch((e: unknown) => {
        if (!alive) return;
        // 503 = 这个功能没配(私有网未接线), 不是故障。两者要能分开显示。
        if (e instanceof HubError && e.status === 503) { setUnavailable(e.message); setError(null); }
        else setError(e instanceof Error ? e.message : String(e));
      })
      // ★ 成败都算"取过一次": 首屏不能因为第一次就失败而永远转圈 —— 那样连
      //   LoadError 都画不出来, 台面上"读失败"与"正在读"又混成一件事。
      .finally(() => { if (alive) { setLoaded(true); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const [tick, setTick] = useState(0);
  useEffect(() => run(), [run, tick, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, loaded, error, unavailable, refetch: () => setTick((t) => t + 1) };
}

// ─── 终端 ─────────────────────────────────────────────────────────────────

const KIND_TO_TYPE: Record<string, Terminal['type']> = {
  dog: 'dog', glasses: 'glasses', phone: 'phone', drone: 'drone', vehicle: 'vehicle',
};

/** 设备注册表里 last_seen 多新算"在线"。hub 每次带令牌的请求都会 touch(30s 节流)。 */
const ONLINE_WINDOW_MS = 3 * 60 * 1000;

/**
 * 遥测的 extra 槽:设备原样塞进来的健康信息(组件启停 + 传感器速率)。
 * hub 明确"不解释"它, 这里也只解析形状 —— 解析失败就当没有, 不猜。
 */
function parseExtra(extra: string | undefined): Pick<Terminal, 'services' | 'sensorHz'> {
  if (!extra) return { services: null, sensorHz: null };
  try {
    const j = JSON.parse(extra) as { services?: Record<string, boolean>; sensors?: Record<string, number> };
    return {
      services: j.services && typeof j.services === 'object' ? j.services : null,
      sensorHz: j.sensors && typeof j.sensors === 'object' ? j.sensors : null,
    };
  } catch {
    return { services: null, sensorHz: null };
  }
}

function toTerminal(d: HubDevice, node: HubNode | undefined, tel: HubTelemetry | undefined): Terminal {
  const lastSeenMs = d.lastSeen ? d.lastSeen * 1000 : 0;
  // 两个独立证据:私有网说它在线,或者它最近拿令牌调过 hub。任一为真即在线。
  const online = node?.online === true || (lastSeenMs > 0 && Date.now() - lastSeenMs < ONLINE_WINDOW_MS);
  // 遥测缺席 → 保持 null。**不要 `tel?.battery ?? 0`** —— 那正是这一版要根除的谎:
  // 没上报和电量为 0 在台面上必须长得不一样。
  const num = (v: number | undefined) => (v === undefined ? null : v);
  return {
    id: d.id,
    name: d.name || d.id,
    type: KIND_TO_TYPE[d.kind] ?? 'other',
    capabilityLevel: (tel?.capability as Terminal['capabilityLevel']) || null,
    status: online ? 'online' : 'offline',
    battery: num(tel?.battery),
    temperature: num(tel?.temperature),
    version: tel?.version || null,
    lastSeen: isoFromUnixSec(d.lastSeen),   // 注册表是秒
    currentTask: null,
    networkType: node ? '私有网' : null,
    overlayIP: node?.ips?.[0] ?? null,
    diskUsage: num(tel?.disk),
    cpuUsage: num(tel?.cpu),
    gpuUsage: num(tel?.gpu),
    memoryUsage: num(tel?.memory),
    revoked: !!d.revokedAt,
    telemetryAt: tel ? isoFromUnixSec(tel.reportedAt) : null,
    poseX: num(tel?.poseX),
    poseY: num(tel?.poseY),
    poseYaw: num(tel?.poseYaw),
    poseMapId: tel?.mapId || null,
    // 0 = hub 从没收到过带位姿的上报 —— 那是"没有", 不是"1970 年"。
    poseAt: tel?.poseAt ? tel.poseAt : null,
    ...parseExtra(tel?.extra),
  };
}

export function useTerminals() {
  const src = useFetch(() => hub.devices());
  const devices = src.data?.devices ?? [];
  const nodes = src.data?.nodes ?? [];
  const telemetry = src.data?.telemetry ?? {};
  const byDevice = new Map<string, HubNode>();
  for (const n of nodes) {
    const key = n.deviceId || n.name;
    if (key) byDevice.set(key, n);
  }
  const terminals = devices.map((d) => toTerminal(d, byDevice.get(d.id), telemetry[d.id]));
  return {
    terminals, nodes,
    /** 有多少台真上报过遥测 —— 台面上任何"平均电量"都得带上这个分母(§9b)。 */
    reporting: Object.keys(telemetry).length,
    /** 读遥测失败 ≠ 大家都没上报。这一条要能单独显示。 */
    telemetryError: src.data?.telemetryError ?? null,
    loading: src.loading, loaded: src.loaded, error: src.error, refetch: src.refetch,
  };
}

// ─── 私有网 ───────────────────────────────────────────────────────────────

export interface NetworkView {
  nodes: HubNode[];
  /** undefined = 没配 wg 网关;[] = 配了没人连;null = 读失败。三态不能压平。 */
  wgPeers: WGPeer[] | undefined;
  wgError: string | null;
  health: { ok: boolean; configured: boolean; detail?: string; note?: string } | null;
}

export function useNetwork(pollMs = 15_000) {
  const [view, setView] = useState<NetworkView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // health 先读:私有网没配时 /net/nodes 会 503,而 health 会明说 configured=false。
        const health = await hub.netHealth();
        if (!health.configured) {
          if (alive) { setUnavailable('私有网未配置(hub 没有 headscale 地址)'); setView({ nodes: [], wgPeers: undefined, wgError: null, health }); }
          return;
        }
        const n = await hub.netNodes();
        if (!alive) return;
        setView({ nodes: n.nodes ?? [], wgPeers: n.wgPeers, wgError: n.wgError ?? null, health });
        setError(null); setUnavailable(null);
      } catch (e) {
        if (!alive) return;
        if (e instanceof HubError && e.status === 503) setUnavailable(e.message);
        else setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, tick]);

  return { view, loading, error, unavailable, refetch: () => setTick((t) => t + 1) };
}

// ─── 任务台账 ─────────────────────────────────────────────────────────────

const UNIT_STATE: Record<string, TaskUnit['status']> = {
  unclaimed: 'unclaimed', claimed: 'claimed', in_progress: 'in_progress',
  split: 'split', done: 'completed', failed: 'failed',
};

function toUnit(u: HubUnit): TaskUnit {
  return {
    id: String(u.id),
    taskId: String(u.taskId),
    name: u.label || `${u.kind}#${u.seq}`,
    status: UNIT_STATE[u.state] ?? 'unclaimed',
    assignee: u.claimedBy || null,
    evidenceRaw: u.evidence,
    result: u.result,
    failReason: u.failReason,
  };
}

/**
 * 任务列表 + 每个任务的工作单元。
 * hub 的 /ledger/tasks 只给任务行,进度要看单元 → 每个任务多一次 /ledger/task。
 * 舰队规模是几十个任务,N+1 在这里是对的:换成一个聚合接口要动 hub,
 * 而进度必须是**数出来的**(done/total),不能让任务行自报一个 progress 字段。
 */
export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskUnits, setTaskUnits] = useState<TaskUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { tasks: rows } = await hub.tasks();
        const details = await Promise.all((rows ?? []).map((t) => hub.task(t.id)));
        if (!alive) return;
        const allUnits: TaskUnit[] = [];
        const mapped: Task[] = (rows ?? []).map((t, i) => {
          const units = (details[i]?.units ?? []).map(toUnit);
          allUnits.push(...units);
          const countable = units.filter((u) => u.status !== 'split');
          const done = countable.filter((u) => u.status === 'completed').length;
          const running = countable.some((u) => u.status === 'claimed' || u.status === 'in_progress');
          const status: Task['status'] =
            t.status === 'done' ? 'completed'
              : t.status === 'failed' ? 'failed'
                : t.status === 'cancelled' ? 'cancelled'
                  : running || done > 0 ? 'in_progress' : 'pending';
          return {
            id: String(t.id),
            name: t.goal,
            description: t.goal,
            status,
            progress: countable.length ? Math.round((done / countable.length) * 100) : 0,
            assignees: [...new Set(units.map((u) => u.assignee).filter((a): a is string => !!a))],
            created: isoFromUnixMs(t.createdAt) ?? '',   // 台账是毫秒
            deadline: null,   // 台账没有截止时间字段 —— 不编一个
            priority: null,   // 台账没有优先级字段
            completedAt: status === 'completed' ? (isoFromUnixMs(t.updatedAt) ?? undefined) : undefined,
          };
        });
        setTasks(mapped); setTaskUnits(allUnits); setError(null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tick]);

  // 台账每次变更 hub 都会 /ws 广播 —— 收到就重拉, 不轮询。
  useEffect(() => {
    let ws: WebSocket | null = null;
    try {
      ws = connectEvents((e) => {
        if ((e as { channel?: string; type?: string })?.channel === 'ledger'
          || (e as { type?: string })?.type === 'ledger') refetch();
      });
    } catch { /* /ws 连不上不影响首屏 */ }
    return () => ws?.close();
  }, [refetch]);

  return { tasks, taskUnits, loading, error, refetch };
}

// ─── 告警 / 审计 / 总览 ────────────────────────────────────────────────────

/**
 * 告警。hub 每 10 秒判定一次(掉线/低电/高温),同一条件只累加不刷屏,
 * 条件消失自动消解。这里只负责显示与"确认"。
 */
export function useAlerts(pollMs = 20_000) {
  const [alerts, setAlerts] = useState<HubAlert[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await hub.alerts();
        if (!alive) return;
        setAlerts(r.alerts ?? []); setCounts(r.counts ?? {}); setError(null); setUnavailable(null);
      } catch (e) {
        if (!alive) return;
        if (e instanceof HubError && e.status === 503) setUnavailable(e.message);
        else setError(e instanceof Error ? e.message : String(e));
      } finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, tick]);

  // hub 判出新告警会广播, 收到就重拉 —— 不靠轮询间隔碰运气
  useEffect(() => {
    let ws: WebSocket | null = null;
    try { ws = connectEvents((e) => { if ((e as { type?: string })?.type === 'alert') refetch(); }); }
    catch { /* /ws 连不上不影响首屏 */ }
    return () => ws?.close();
  }, [refetch]);

  const ack = useCallback(async (id: number) => {
    await hub.ackAlert(id);
    refetch();
  }, [refetch]);

  return { alerts, counts, loading, error, unavailable, refetch, ack };
}

/** 总览聚合。每个比率都自带分母(battery.reporting / battery.total)。 */
export function useFleetSummary(pollMs = 20_000) {
  const [summary, setSummary] = useState<HubSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await hub.summary();
        if (alive) { setSummary(r); setError(null); }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, tick]);

  return { summary, loading, error, refetch: () => setTick((t) => t + 1) };
}

// ─── 视频墙的名单 ─────────────────────────────────────────────────────────

/**
 * 中继上有哪几路可看。三态照旧, 但这里的三态各有确切处置:
 *   unavailable(503) 中继没部署 → 去 panda02 跑 deploy_video_relay.sh
 *   error(502)       中继不可达 → 服务挂了/够不到设备, 去查那台机器
 *   devices=[]       中继在跑但一路没配 → 改 go2rtc-relay.yaml
 * 压成一句"没有视频"就等于把三种完全不同的处置画成同一张脸(§9b)。
 *
 * ★ 轮询只更新【名单和观看者计数】, 不碰媒体。画面是每格自己的 WebRTC,
 *   不会因为这里刷新而重连。
 */
export function useVideoStreams(pollMs = 15_000) {
  const [devices, setDevices] = useState<HubVideoDevice[] | null>(null);
  const [unassigned, setUnassigned] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await hub.videoStreams();
        if (!alive) return;
        setDevices(r.devices ?? []);
        setUnassigned(r.unassigned ?? []);
        setError(null); setUnavailable(null);
      } catch (e) {
        if (!alive) return;
        if (e instanceof HubError && e.status === 503) { setUnavailable(e.message); setError(null); }
        else setError(e instanceof Error ? e.message : String(e));
      } finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, tick]);

  return { devices, unassigned, loading, error, unavailable, refetch: () => setTick((t) => t + 1) };
}

// ─── 现场报表 ─────────────────────────────────────────────────────────────

/** 各设备的现场报表。unavailable = hub 没有 fleet.db(那几条接口 503)。 */
export function useReports(pollMs = 60_000) {
  const [reports, setReports] = useState<HubDeviceReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await hub.reports();
        if (!alive) return;
        setReports(r.reports ?? []); setError(null); setUnavailable(null);
      } catch (e) {
        if (!alive) return;
        if (e instanceof HubError && e.status === 503) { setUnavailable(e.message); setError(null); }
        else setError(e instanceof Error ? e.message : String(e));
      } finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, tick]);

  return { reports, loading, error, unavailable, refetch: () => setTick((t) => t + 1) };
}

// ─── 实时位姿 ─────────────────────────────────────────────────────────────

/**
 * 设备的【此刻】位姿。两个来源合成一条:
 *   · 首屏:GET /devices 里的 livePoses(hub 内存里还新鲜的那些)
 *   · 之后:/ws 上的 pose 事件, 设备每动一下就来一条(站着不动时 5 秒一条保活)
 *
 * ★ 它和遥测里那份位姿是【两回事】, 页面必须能说出自己在看哪一条:
 *   实时流断了要退回遥测的"最后已知位置", 而不是让屏幕上那个机器人停在原地
 *   假装一切正常 —— 2026-08-08 位姿门闩死 44 分钟, 现场看到的就是后者。
 */
export function useLivePoses() {
  const [poses, setPoses] = useState<Record<string, HubLivePose>>({});

  // 首屏种子:不等第一条广播, 打开就能看到机器人在哪。
  useEffect(() => {
    let alive = true;
    hub.devices()
      .then((r) => { if (alive && r.livePoses) setPoses(r.livePoses); })
      .catch(() => { /* 设备列表那条链路自己会报错, 这里不重复报 */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    try {
      ws = connectEvents((e) => {
        const ev = e as { type?: string; data?: HubLivePose };
        if (ev?.type === 'pose' && ev.data?.deviceId) {
          setPoses((prev) => ({ ...prev, [ev.data!.deviceId]: ev.data! }));
        }
      });
    } catch { /* /ws 连不上时只是没有实时流, 页面照常用遥测那条 */ }
    return () => ws?.close();
  }, []);

  return poses;
}

// ─── 地图栅格 ─────────────────────────────────────────────────────────────

/**
 * 一台设备某张图的占据栅格。返回可直接画的 ImageBitmap + 它的几何。
 *
 * ★ 为什么要 fetch 而不是 <img src>: hub 除 /health 外全要 Bearer, 而浏览器发
 *   <img> 请求不会带头 —— 直接用 src 会稳定地 401, 而且是静默的(图裂了而已)。
 */
/** 有哪些图推过栅格(元信息, 不含像素)。单独一个 hook, 因为"有哪些图"要先于
 *  "看哪张图"决定 —— 合在一起就成了鸡生蛋:图号要用元信息算, 元信息又按图号取。 */
export function useMapGridList(pollMs = 60_000) {
  const [metas, setMetas] = useState<HubMapGrid[] | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await hub.mapGrids();
        if (alive) setMetas(r.grids ?? []);
      } catch { /* 读不到元信息不该让整页红:下面取图那步会报出来 */ }
    };
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);
  return metas;
}

/**
 * 取某张图的栅格像素。
 *
 * ★ 为什么是 fetch 而不是 <img src>: hub 除 /health 外全要 Bearer, 而浏览器发
 *   <img> 请求不带头 —— 直接用 src 会稳定地 401, 且是静默的(只是图裂了)。
 * ★ 只在 updatedAt 变化时重新下载:内容没变时每分钟重传几 KB 没有意义。
 */
export function useMapGridImage(mapId: string | null, metas: HubMapGrid[] | null) {
  const [grid, setGrid] = useState<{ bitmap: ImageBitmap; meta: HubMapGrid } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const meta = metas?.find((g) => g.mapId === mapId) ?? null;
  const stamp = meta ? `${meta.deviceId}:${meta.mapId}:${meta.updatedAt}` : '';

  useEffect(() => {
    if (!meta) { setGrid(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(mapGridURL(meta.deviceId, meta.mapId), {
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bmp = await createImageBitmap(await res.blob());
        if (!alive) { bmp.close(); return; }
        setGrid((prev) => { prev?.bitmap.close(); return { bitmap: bmp, meta }; });
        setError(null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { alive = false; };
  }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps

  return { grid, error };
}

/** 审计。只给 admin 看(设备令牌会被 403)。 */
export function useAudit(limit = 100) {
  const src = useFetch(() => hub.audit(limit), [limit]);
  return {
    entries: (src.data?.entries ?? []) as HubAuditEntry[],
    loading: src.loading, error: src.error, unavailable: src.unavailable, refetch: src.refetch,
  };
}

/**
 * 控制台里的【人】及其 passkey。
 *
 * 三态照旧:503 = hub 没配 passkey(--auth-db / RP_ID 缺一)—— 那不是故障,
 * 是这套还没启用, 页面要说得出区别。
 *
 * ★ 吊销和停用都**立刻重取一次**。不重取的话页面会停在吊销前那份名单上,
 *   而"我点了吊销"和"它真的被吊销了"是两件事 —— 让人看着旧名单以为没生效,
 *   于是再点一次, 是这类界面最常见的假故障。
 */
export function usePeople() {
  const src = useFetch(() => hub.auth.people(), []);
  const revokeCredential = useCallback(async (id: string) => {
    await hub.auth.revokeCredential(id);
    src.refetch();
  }, [src]);
  const disablePerson = useCallback(async (id: string) => {
    await hub.auth.disablePerson(id);
    src.refetch();
  }, [src]);
  return {
    people: src.data?.people ?? [],
    credentials: src.data?.credentials ?? [],
    loading: src.loading, error: src.error, unavailable: src.unavailable,
    refetch: src.refetch, revokeCredential, disablePerson,
  };
}

// ─── 还没有数据源的那几页(§3 表格里的 🔴)────────────────────────────────

/**
 * 知识图谱(单体/群体)。数据是设备经 ksync 推上来的【PIPL 过滤后】快照:
 * 只有地点/物体/事实 —— 人物、对话、照片永远不出设备(狗侧过滤 + hub 白名单)。
 *
 * device 为空 = 群体(全设备并集);给 device id = 单体。
 * 三态纪律照旧:503 = hub 没配知识库(unavailable);设备注册了但没推过 =
 * neverPushed(页面上是"待首次上报",不是"暂无知识")。
 */
export function useKnowledge(device?: string) {
  const [graph, setGraph] = useState<HubKnowledgeGraph | null>(null);
  const [summary, setSummary] = useState<HubKnowledgeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [g, s] = await Promise.all([
          hub.knowledgeGraph(device || undefined),
          hub.knowledgeSummary(),
        ]);
        if (!alive) return;
        setGraph(g); setSummary(s); setError(null); setUnavailable(null);
      } catch (e) {
        if (!alive) return;
        if (e instanceof HubError && e.status === 503) { setUnavailable(e.message); setError(null); }
        else setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [device, tick]);

  // 设备每次推快照 hub 都广播 knowledge 事件 —— 收到即重拉,不轮询。
  useEffect(() => {
    let ws: WebSocket | null = null;
    try {
      ws = connectEvents((e) => {
        if ((e as { channel?: string; type?: string })?.channel === 'knowledge'
          || (e as { type?: string })?.type === 'knowledge') refetch();
      });
    } catch { /* /ws 连不上不影响首屏 */ }
    return () => ws?.close();
  }, [refetch]);

  return { graph, summary, loading, error, unavailable, refetch };
}

export function useOps() {
  const reason = '运维基础设施尚不存在(P4:OTA/配置下发/远程日志)。版本矩阵需要设备上报版本';
  return {
    versionMatrix: [], otaJobs: [], configItems: [], logEntries: [], diagnosticReports: [],
    loading: false, error: null, unavailable: reason, refetch: () => {},
  };
}
